import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader2,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  Building2,
  Shield,
  User,
  UserCog,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
} from "@/components/ui/dialog";
import { trpc } from "@/utils/trpc";
import { toast } from "sonner";

interface EditUserDialogProps {
  user: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// SAP Fiori-style Form Field Component
function FormField({ 
  label, 
  required, 
  children,
  hint,
}: { 
  label: string; 
  required?: boolean; 
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#2C2C2C]">
        {label}{required && <span className="text-[#FF6B6B] ml-0.5">*</span>}:
      </Label>
      {children}
      {hint && <p className="text-xs text-[#6B6B6B]">{hint}</p>}
    </div>
  );
}

// Brand-compliant Section Header with purple left border
function SectionHeader({ title }: { title: string }) {
  return (
    <div className="border-l-4 border-[#5B2C93] pl-3 mb-4">
      <h3 className="text-base font-medium text-[#5B2C93]">{title}</h3>
    </div>
  );
}

export default function EditUserDialog({ user, open, onOpenChange, onSuccess }: EditUserDialogProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("general");

  const tabs = useMemo(() => [
    { id: "general", label: t("users.edit.generalInformation"), icon: User },
    { id: "organization", label: t("users.edit.organization"), icon: Building2 },
    { id: "access", label: t("users.edit.accessSecurity"), icon: Shield },
  ], [t]);
  
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    firstNameAr: "",
    lastNameAr: "",
    email: "",
    phone: "",
    jobTitle: "",
    employeeId: "",
    departmentId: null as number | null,
    managerId: null as number | null,
    contractorCompanyId: null as number | null,
    subContractorCompanyId: null as number | null,
    contractReference: "",
    contractExpiry: "",
    reportingToId: null as number | null,
    clientCompanyId: null as number | null,
    accountManagerId: null as number | null,
    roleId: null as number | null,
    status: "active" as "active" | "inactive" | "suspended",
    selectedSiteId: null as number | null,
    selectedZoneId: null as number | null,
    selectedAreaId: null as number | null,
    allSitesAccess: true,
    selectedSiteIds: [] as number[],
    accountExpiresWithContract: false,
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        firstNameAr: user.firstNameAr || "",
        lastNameAr: user.lastNameAr || "",
        email: user.email || "",
        phone: user.phone || "",
        jobTitle: user.jobTitle || "",
        employeeId: user.employeeId || "",
        departmentId: user.departmentId || null,
        managerId: user.managerId || null,
        contractorCompanyId: user.contractorCompanyId || null,
        subContractorCompanyId: user.subContractorCompanyId || null,
        contractReference: user.contractReference || "",
        contractExpiry: user.contractExpiry ? new Date(user.contractExpiry).toISOString().split('T')[0] : "",
        reportingToId: user.reportingToId || null,
        clientCompanyId: user.clientCompanyId || null,
        accountManagerId: user.accountManagerId || null,
        roleId: user.roleId || null,
        status: user.status || "active",
        selectedSiteId: null,
        selectedZoneId: null,
        selectedAreaId: null,
        allSitesAccess: user.allSitesAccess !== false,
        selectedSiteIds: user.siteIds || [],
        accountExpiresWithContract: user.accountExpiresWithContract || false,
      });
      setActiveTab("general");
    }
  }, [user]);

  const { data: departmentsData } = trpc.departments.list.useQuery({});
  const { data: usersData } = trpc.users.list.useQuery({});
  const { data: sitesData } = trpc.sites.getAll.useQuery();
  const { data: companiesData } = trpc.masterData.getAllCompanies.useQuery({});
  const { data: rolesData } = trpc.roles.list.useQuery();
  const { data: zonesData } = trpc.zones.getAll.useQuery({ siteId: formData.selectedSiteId || undefined });
  const { data: areasData } = trpc.masterData.getAreaTypes.useQuery();

  const departments = departmentsData || [];
  const users = usersData?.users || [];
  const sites = sitesData || [];
  const companies = companiesData || [];
  const roles = rolesData || [];
  const zones = zonesData || [];
  const areas = areasData || [];

  const contractorCompanies = useMemo(() => companies.filter((c: any) => c.type === "contractor"), [companies]);
  const subContractorCompanies = useMemo(() => companies.filter((c: any) => c.type === "subcontractor"), [companies]);
  const clientCompanies = useMemo(() => companies.filter((c: any) => c.type === "client"), [companies]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, selectedZoneId: null, selectedAreaId: null }));
  }, [formData.selectedSiteId]);

  useEffect(() => {
    setFormData(prev => ({ ...prev, selectedAreaId: null }));
  }, [formData.selectedZoneId]);

  const updateUserMutation = trpc.users.update.useMutation({
    onSuccess: () => {
      toast.success(t("users.edit.userUpdated"));
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast.error(t("users.edit.updateFailed"), { description: error.message });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.firstName || !formData.lastName) {
      toast.error(t("users.edit.enterFirstLastName"));
      setActiveTab("general");
      return;
    }
    if (!formData.email) {
      toast.error(t("users.edit.enterEmail"));
      setActiveTab("general");
      return;
    }
    updateUserMutation.mutate({
      id: user.id,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone || undefined,
      jobTitle: formData.jobTitle || undefined,
      roleId: formData.roleId || undefined,
      status: formData.status,
      employeeId: formData.employeeId || undefined,
      departmentId: formData.departmentId || undefined,
      managerId: formData.managerId || undefined,
      contractorCompanyId: formData.contractorCompanyId || undefined,
      contractReference: formData.contractReference || undefined,
      contractExpiry: formData.contractExpiry || undefined,
      reportingToId: formData.reportingToId || undefined,
      clientCompanyId: formData.clientCompanyId || undefined,
      accountManagerId: formData.accountManagerId || undefined,
      accountExpiresWithContract: formData.accountExpiresWithContract,
    });
  };

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const isFirstTab = currentTabIndex === 0;
  const isLastTab = currentTabIndex === tabs.length - 1;

  const goToNextTab = () => { if (!isLastTab) setActiveTab(tabs[currentTabIndex + 1].id); };
  const goToPrevTab = () => { if (!isFirstTab) setActiveTab(tabs[currentTabIndex - 1].id); };

  const getUserTypeDisplay = () => {
    if (!user) return "";
    switch (user.userType) {
      case "centre3_employee": return t("users.userTypes.centre3Employee");
      case "contractor": return t("users.userTypes.contractor");
      case "sub_contractor": return t("users.userTypes.subContractor");
      case "client": return t("users.userTypes.client");
      default: return user.userType || "";
    }
  };

  const isSubContractor = user?.userType === "sub_contractor";

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] p-0 overflow-hidden" showCloseButton={false}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#5B2C93] flex items-center justify-center">
                <UserCog className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-medium text-[#5B2C93]">{t("users.edit.editUser")}</h1>
                <p className="text-sm text-[#6B6B6B]">{getUserTypeDisplay()}</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
          </div>

          {/* Main Content with Left Sidebar Tabs */}
          <div className="flex flex-1 overflow-hidden">
            {/* Left Sidebar - Vertical Tabs */}
            <div className="w-56 border-r bg-white flex flex-col">
              <div className="p-4 border-b">
                <p className="text-sm font-medium text-[#2C2C2C] tracking-wider">{t("users.edit.sections")}</p>
              </div>
              <nav className="flex-1 p-2">
                {tabs.map((tab, index) => {
                  const isActive = activeTab === tab.id;
                  const isCompleted = index < currentTabIndex;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors mb-1 ${
                        isActive 
                          ? "bg-[#E8DCF5] text-[#5B2C93] border-l-4 border-[#5B2C93]" 
                          : isCompleted
                            ? "text-[#2C2C2C] hover:bg-[#F5F5F5]"
                            : "text-[#6B6B6B] hover:bg-[#F5F5F5] hover:text-[#2C2C2C]"
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                        isActive 
                          ? "bg-[#5B2C93] text-white"
                          : isCompleted
                            ? "bg-[#D1FAE5] text-white"
                            : "bg-[#F5F5F5] text-[#6B6B6B]"
                      }`}>
                        {isCompleted ? <CheckCircle className="h-4 w-4" /> : index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{tab.label}</p>
                      </div>
                      {isActive && <ChevronRight className="h-4 w-4 text-[#5B2C93]" />}
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 flex flex-col min-h-0">
              <ScrollArea className="flex-1 min-h-0">
                <div className="p-6">
                  {/* General Information Tab */}
                  {activeTab === "general" && (
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg border p-6">
                        <SectionHeader title={t("users.edit.personalData")} />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                          <FormField label={t("users.edit.firstNameEn")} required>
                            <Input value={formData.firstName}
                              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              placeholder={t("users.edit.enterFirstName")} className="bg-[#F5F5F5]" />
                          </FormField>
                          <FormField label={t("users.edit.lastNameEn")} required>
                            <Input value={formData.lastName}
                              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              placeholder={t("users.edit.enterLastName")} className="bg-[#F5F5F5]" />
                          </FormField>
                          <FormField label={t("users.edit.firstNameAr")}>
                            <Input value={formData.firstNameAr}
                              onChange={(e) => setFormData({ ...formData, firstNameAr: e.target.value })}
                              placeholder="أدخل الاسم الأول" dir="rtl" className="bg-[#F5F5F5]" />
                          </FormField>
                          <FormField label={t("users.edit.lastNameAr")}>
                            <Input value={formData.lastNameAr}
                              onChange={(e) => setFormData({ ...formData, lastNameAr: e.target.value })}
                              placeholder="أدخل اسم العائلة" dir="rtl" className="bg-[#F5F5F5]" />
                          </FormField>
                        </div>
                      </div>
                      <div className="bg-white rounded-lg border p-6">
                        <SectionHeader title={t("users.edit.communicationData")} />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                          <FormField label={t("users.edit.emailAddress")} required>
                            <Input type="email" value={formData.email}
                              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                              placeholder="user@example.com" className="bg-[#F5F5F5]" />
                          </FormField>
                          <FormField label={t("users.edit.mobileNumber")}>
                            <Input value={formData.phone}
                              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                              placeholder="+966 5XX XXX XXXX" className="bg-[#F5F5F5]" />
                          </FormField>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Organization Tab */}
                  {activeTab === "organization" && (
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg border p-6">
                        <SectionHeader title={t("users.edit.employmentData")} />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                          <FormField label={t("users.edit.jobTitle")}>
                            <Input value={formData.jobTitle}
                              onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                              placeholder={t("users.edit.enterJobTitle")} className="bg-[#F5F5F5]" />
                          </FormField>
                          <FormField label={t("users.edit.department")}>
                            <Select value={formData.departmentId?.toString() || ""}
                              onValueChange={(value) => setFormData({ ...formData, departmentId: value ? parseInt(value) : null })}>
                              <SelectTrigger className="bg-[#F5F5F5]">
                                <SelectValue placeholder={t("users.edit.selectDepartment")} />
                              </SelectTrigger>
                              <SelectContent>
                                {departments.map((dept: any) => (
                                  <SelectItem key={dept.id} value={dept.id.toString()}>{dept.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormField>
                          {user.userType === "centre3_employee" && (
                            <>
                              <FormField label={t("users.edit.employeeId")}>
                                <Input value={formData.employeeId}
                                  onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                                  placeholder={t("users.edit.enterEmployeeId")} className="bg-[#F5F5F5]" />
                              </FormField>
                              <FormField label={t("users.edit.reportsTo")}>
                                <Select value={formData.managerId?.toString() || ""}
                                  onValueChange={(value) => setFormData({ ...formData, managerId: value ? parseInt(value) : null })}>
                                  <SelectTrigger className="bg-[#F5F5F5]">
                                    <SelectValue placeholder={t("users.edit.selectManager")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {users.filter((u: any) => u.id !== user.id).map((u: any) => (
                                      <SelectItem key={u.id} value={u.id.toString()}>
                                        {u.name || `${u.firstName} ${u.lastName}`}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormField>
                            </>
                          )}
                        </div>
                      </div>

                      {(user.userType === "contractor" || user.userType === "sub_contractor") && (
                        <div className="bg-white rounded-lg border p-6">
                          <SectionHeader title={t("users.edit.contractorAssignment")} />
                          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                            <FormField label={isSubContractor ? t("users.edit.parentContractorCompany") : t("users.edit.contractorCompany")}>
                              <Select value={formData.contractorCompanyId?.toString() || ""}
                                onValueChange={(value) => setFormData({ ...formData, contractorCompanyId: value ? parseInt(value) : null })}>
                                <SelectTrigger className="bg-[#F5F5F5]">
                                  <SelectValue placeholder={t("users.edit.selectCompany")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {contractorCompanies.map((company: any) => (
                                    <SelectItem key={company.id} value={company.id.toString()}>{company.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormField>
                            {isSubContractor && (
                              <FormField label={t("users.edit.subContractorCompany")}>
                                <Select value={formData.subContractorCompanyId?.toString() || ""}
                                  onValueChange={(value) => setFormData({ ...formData, subContractorCompanyId: value ? parseInt(value) : null })}>
                                  <SelectTrigger className="bg-[#F5F5F5]">
                                    <SelectValue placeholder={t("users.edit.selectSubContractor")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {subContractorCompanies.map((company: any) => (
                                      <SelectItem key={company.id} value={company.id.toString()}>{company.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormField>
                            )}
                            <FormField label={t("users.edit.contractReference")}>
                              <Input value={formData.contractReference}
                                onChange={(e) => setFormData({ ...formData, contractReference: e.target.value })}
                                placeholder={t("users.edit.enterContractRef")} className="bg-[#F5F5F5]" />
                            </FormField>
                            <FormField label={t("users.edit.contractExpiryDate")}>
                              <Input type="date" value={formData.contractExpiry}
                                onChange={(e) => setFormData({ ...formData, contractExpiry: e.target.value })}
                                className="bg-[#F5F5F5]" />
                            </FormField>
                            <FormField label={t("users.edit.centre3ContactPerson")}>
                              <Select value={formData.reportingToId?.toString() || ""}
                                onValueChange={(value) => setFormData({ ...formData, reportingToId: value ? parseInt(value) : null })}>
                                <SelectTrigger className="bg-[#F5F5F5]">
                                  <SelectValue placeholder={t("users.edit.selectContact")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((u: any) => (
                                    <SelectItem key={u.id} value={u.id.toString()}>
                                      {u.name || `${u.firstName} ${u.lastName}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormField>
                          </div>
                        </div>
                      )}

                      {user.userType === "client" && (
                        <div className="bg-white rounded-lg border p-6">
                          <SectionHeader title={t("users.edit.clientAssignment")} />
                          <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                            <FormField label={t("users.edit.clientCompany")}>
                              <Select value={formData.clientCompanyId?.toString() || ""}
                                onValueChange={(value) => setFormData({ ...formData, clientCompanyId: value ? parseInt(value) : null })}>
                                <SelectTrigger className="bg-[#F5F5F5]">
                                  <SelectValue placeholder={t("users.edit.selectClientCompany")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {clientCompanies.map((company: any) => (
                                    <SelectItem key={company.id} value={company.id.toString()}>{company.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormField>
                            <FormField label={t("users.edit.accountManagerCentre3")}>
                              <Select value={formData.accountManagerId?.toString() || ""}
                                onValueChange={(value) => setFormData({ ...formData, accountManagerId: value ? parseInt(value) : null })}>
                                <SelectTrigger className="bg-[#F5F5F5]">
                                  <SelectValue placeholder={t("users.edit.selectAccountManager")} />
                                </SelectTrigger>
                                <SelectContent>
                                  {users.map((u: any) => (
                                    <SelectItem key={u.id} value={u.id.toString()}>
                                      {u.name || `${u.firstName} ${u.lastName}`}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </FormField>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Access & Security Tab */}
                  {activeTab === "access" && (
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg border p-6">
                        <SectionHeader title={t("users.edit.userRoleStatus")} />
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                          <FormField label={t("users.edit.systemRole")} required>
                            <Select value={formData.roleId?.toString() || ""}
                              onValueChange={(value) => setFormData({ ...formData, roleId: value ? parseInt(value) : null })}>
                              <SelectTrigger className="bg-[#F5F5F5]">
                                <SelectValue placeholder={t("users.edit.selectRole")} />
                              </SelectTrigger>
                              <SelectContent>
                                {roles.map((role: any) => (
                                  <SelectItem key={role.id} value={role.id.toString()}>{role.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </FormField>
                          <FormField label={t("users.edit.accountStatus")} required>
                            <Select value={formData.status}
                              onValueChange={(value: "active" | "inactive" | "suspended") => setFormData({ ...formData, status: value })}>
                              <SelectTrigger className="bg-[#F5F5F5]">
                                <SelectValue placeholder={t("users.edit.selectStatus")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="active">{t("common.active")}</SelectItem>
                                <SelectItem value="inactive">{t("common.inactive")}</SelectItem>
                                <SelectItem value="suspended">{t("users.edit.suspended")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </FormField>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg border p-6">
                        <SectionHeader title={t("users.edit.siteAccess")} />
                        <div className="space-y-4">
                          <div className="flex items-center gap-3">
                            <Checkbox id="allSites" checked={formData.allSitesAccess}
                              onCheckedChange={(checked) => setFormData({ 
                                ...formData, allSitesAccess: !!checked,
                                selectedSiteId: null, selectedZoneId: null, selectedAreaId: null,
                              })} />
                            <Label htmlFor="allSites" className="cursor-pointer">
                              {t("users.edit.grantAllSitesAccess")}
                            </Label>
                          </div>
                          {!formData.allSitesAccess && (
                            <div className="grid grid-cols-3 gap-6 pt-4">
                              <FormField label={t("users.edit.site")}>
                                <Select value={formData.selectedSiteId?.toString() || ""}
                                  onValueChange={(value) => setFormData({ ...formData, selectedSiteId: value ? parseInt(value) : null })}>
                                  <SelectTrigger className="bg-[#F5F5F5]">
                                    <SelectValue placeholder={t("users.edit.selectSite")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {sites.map((site: any) => (
                                      <SelectItem key={site.id} value={site.id.toString()}>{site.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormField>
                              <FormField label={t("users.edit.zone")}>
                                <Select value={formData.selectedZoneId?.toString() || ""}
                                  onValueChange={(value) => setFormData({ ...formData, selectedZoneId: value ? parseInt(value) : null })}
                                  disabled={!formData.selectedSiteId}>
                                  <SelectTrigger className="bg-[#F5F5F5]">
                                    <SelectValue placeholder={formData.selectedSiteId ? t("users.edit.selectZone") : t("users.edit.selectSiteFirst")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {zones.map((zone: any) => (
                                      <SelectItem key={zone.id} value={zone.id.toString()}>{zone.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormField>
                              <FormField label={t("users.edit.area")}>
                                <Select value={formData.selectedAreaId?.toString() || ""}
                                  onValueChange={(value) => setFormData({ ...formData, selectedAreaId: value ? parseInt(value) : null })}
                                  disabled={!formData.selectedZoneId}>
                                  <SelectTrigger className="bg-[#F5F5F5]">
                                    <SelectValue placeholder={formData.selectedZoneId ? t("users.edit.selectArea") : t("users.edit.selectZoneFirst")} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {areas.map((area: any) => (
                                      <SelectItem key={area.id} value={area.id.toString()}>{area.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </FormField>
                            </div>
                          )}
                        </div>
                      </div>

                      {(user.userType === "contractor" || user.userType === "sub_contractor") && (
                        <div className="bg-white rounded-lg border p-6">
                          <SectionHeader title={t("users.edit.accountOptions")} />
                          <div className="flex items-center gap-3">
                            <Checkbox id="expiresWithContract" checked={formData.accountExpiresWithContract}
                              onCheckedChange={(checked) => setFormData({ ...formData, accountExpiresWithContract: !!checked })} />
                            <Label htmlFor="expiresWithContract" className="cursor-pointer">
                              {t("users.edit.accountExpiresWithContract")}
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </ScrollArea>

              {/* Bottom Navigation Bar */}
              <div className="border-t bg-white px-6 py-4 flex items-center justify-between">
                <div>
                  {!isFirstTab && (
                    <Button variant="outline" onClick={goToPrevTab} className="gap-2">
                      <ChevronLeft className="h-4 w-4" />
                      {t("common.previous")}
                    </Button>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {!isLastTab ? (
                    <Button onClick={goToNextTab} className="gap-2 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white">
                      {t("common.next")}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button onClick={handleSubmit} disabled={updateUserMutation.isPending}
                      className="gap-2 bg-[#5B2C93] hover:bg-[#3D1C5E] text-white">
                      {updateUserMutation.isPending ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> {t("users.edit.saving")}</>
                      ) : t("common.saveChanges")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
